// src/components/admin/student/studentStyles.js
import { STUDENT_STATUS_OPTIONS } from "../../../utils/studentLifecycle";

export const UI = {
  page: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#f6f7fb",
    padding: 24,
    boxSizing: "border-box",
  },
  topBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
    flexWrap: "wrap",
  },
  title: { fontSize: 22, fontWeight: 900, color: "#0f172a", letterSpacing: "-0.2px" },
  toolbar: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },

  btn: (variant = "default") => {
    const base = {
      borderRadius: 12,
      padding: "10px 12px",
      fontSize: 13,
      fontWeight: 900,
      cursor: "pointer",
      border: "1px solid #e2e8f0",
      background: "#fff",
      color: "#0f172a",
    };
    if (variant === "primary") return { ...base, background: "#2563eb", borderColor: "#2563eb", color: "#fff" };
    if (variant === "danger") return { ...base, background: "#fff", borderColor: "#fecaca", color: "#b91c1c" };
    return base;
  },

  input: {
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    background: "#fff",
    padding: "10px 12px",
    fontSize: 13,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  select: {
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    background: "#fff",
    padding: "10px 12px",
    fontSize: 13,
    outline: "none",
  },

  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    boxShadow: "0 10px 26px rgba(15,23,42,0.06)",
    overflow: "hidden",
  },

  tableContainer: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    boxShadow: "0 10px 26px rgba(15,23,42,0.06)",
    overflow: "hidden",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  tableHeader: {
    padding: "12px 14px",
    borderBottom: "1px solid #e5e7eb",
    background: "#fbfcfe",
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    fontSize: 12,
    fontWeight: 900,
    color: "#64748b",
    letterSpacing: "0.03em",
    padding: "12px 14px",
    borderBottom: "1px solid #eef2f7",
    background: "#fbfcfe",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  td: {
    padding: "12px 14px",
    borderBottom: "1px solid #f1f5f9",
    fontSize: 13,
    color: "#0f172a",
    verticalAlign: "middle",
  },
  row: { cursor: "pointer" },

  // Detail
  detailShell: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    background: "#f6f7fb",
    padding: 24,
    boxSizing: "border-box",
  },
  detailHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
    background: "#fff",
    fontWeight: 900,
    fontSize: 13,
    color: "#0f172a",
  },
  detailTitle: { fontSize: 20, fontWeight: 900, color: "#0f172a", margin: 0 },

  detailBody: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    boxShadow: "0 10px 26px rgba(15,23,42,0.06)",
    overflow: "hidden",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    position: "relative",
  },
  scroll: { padding: 18, overflowY: "auto", minHeight: 0 },

  sectionTitle: { fontSize: 13, fontWeight: 900, color: "#0f172a", marginBottom: 10 },

  field: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 },
  label: { fontSize: 12, fontWeight: 900, color: "#334155" },

  // Sticky note
  stickyNote: {
    borderRadius: 16,
    border: "1px solid #fde68a",
    background: "#fffbeb",
    boxShadow: "0 10px 22px rgba(245,158,11,0.10)",
    padding: 14,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  stickyTextArea: {
    border: "none",
    outline: "none",
    resize: "none",
    width: "100%",
    background: "transparent",
    fontSize: 13,
    lineHeight: 1.55,
    color: "#0f172a",
    fontFamily: "inherit",
  },
};

// Status colors (more explicit per-status)
export const STATUS_OPTIONS = STUDENT_STATUS_OPTIONS;

export function statusStyle(status) {
  const raw = String(status || "Signed Up").trim();
  // normalize case (supports old data like "active")
  const normalized =
    STATUS_OPTIONS.find((opt) => opt.toLowerCase() === raw.toLowerCase()) || raw;
  const s = normalized;
  const map = {
    Enrolled: {
      bg: "#dcfce7",
      fg: "#166534",
      bd: "#22c55e",
    },

    "Signed Up": {
      bg: "#e0f2fe",
      fg: "#075985",
      bd: "#0284c7",
    },

    Paused: {
      bg: "#ffedd5",
      fg: "#9a3412",
      bd: "#fb923c",
    },

    "No Answer": {
      bg: "#f1f5f9",
      fg: "#334155",
      bd: "#94a3b8",
    },

    Finished: {
      bg: "#ffe4e6",
      fg: "#9f1239",
      bd: "#fb7185",
    },

    "Not Interested": {
      bg: "#fee2e2",
      fg: "#991b1b",
      bd: "#ef4444",
    },
  };

  const c = map[s] || map["No Answer"];
  return {
    background: c.bg,
    color: c.fg,
    border: `1px solid ${c.bd}`,
    borderRadius: 12,
    fontWeight: 900,
  };
}

export function badgeStyle(status) {
  const base = statusStyle(status);
  return {
    ...base,
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12,
  };
}
